import React, { useEffect, useState } from "react"

import request from "~/lib/request"
import { useToast } from "~/utils/Toast"

type StripeConnect = {
  stripe_connected_account_id?: string | null
  stripe_connect_account_type?: string | null
  stripe_connect_connected_at?: string | null
  stripe_connect_last_error?: string | null
  stripe_connect_status:
    | "not_connected"
    | "onboarding"
    | "connected"
    | "connection_error"
    | "disconnected"
}

type StripeConnectSectionProps = {
  venueSlug: string
}

const statusLabels: Record<StripeConnect["stripe_connect_status"], string> = {
  connected: "Connected",
  connection_error: "Connection Error",
  disconnected: "Not Connected",
  not_connected: "Not Connected",
  onboarding: "Onboarding",
}

const StripeConnectSection = ({ venueSlug }: StripeConnectSectionProps) => {
  const toast = useToast()
  const [stripeConnect, setStripeConnect] = useState<StripeConnect | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const basePath = `/api/${venueSlug}/stripe_connect`

  const loadStatus = async () => {
    setIsLoading(true)

    try {
      const response = await request({
        path: `${basePath}/status`,
        method: "GET",
        data: null,
        asFormData: false,
      })
      setStripeConnect(response.stripeConnect)
    } catch (response: any) {
      toast(response.errors?.join(", ") || "Unable to load Stripe connection status.", "danger")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
  }, [venueSlug])

  const connectAccount = async () => {
    setIsSubmitting(true)

    try {
      const response = await request({
        path: `${basePath}/connect_existing`,
        method: "POST",
        data: null,
        asFormData: false,
      })
      window.location.assign(response.onboardingUrl)
    } catch (response: any) {
      toast(response.errors?.join(", ") || "Unable to start Stripe connection.", "danger")
      setIsSubmitting(false)
    }
  }

  const disconnect = async () => {
    setIsSubmitting(true)

    try {
      const response = await request({
        path: `${basePath}/disconnect`,
        method: "POST",
        data: null,
        asFormData: false,
      })
      setStripeConnect(response.stripeConnect)
    } catch (response: any) {
      toast(response.errors?.join(", ") || "Unable to disconnect Stripe.", "danger")
    } finally {
      setIsSubmitting(false)
    }
  }

  const connected = stripeConnect?.stripe_connect_status === "connected"
  const hasConnectedAccount = !!stripeConnect?.stripe_connected_account_id
  const requiresSetup = ["connection_error", "onboarding"].includes(
    stripeConnect?.stripe_connect_status || "not_connected"
  )

  return (
    <section className="border-top mt-4 pt-4">
      <h5 className="mb-2">Stripe Connect</h5>
      <p className="mb-2">
        Connect Stripe to accept new payments directly into this venue&apos;s Stripe account. Stripe
        processing costs and Catch&apos;s configured fee are deducted from the venue&apos;s
        proceeds.
      </p>

      {isLoading ? (
        <span>Loading connection status…</span>
      ) : (
        <>
          <p className="mb-1">
            <strong>Status:</strong>{" "}
            {statusLabels[stripeConnect?.stripe_connect_status || "not_connected"]}
          </p>
          {stripeConnect?.stripe_connect_last_error && (
            <p className="text-danger mb-2">{stripeConnect.stripe_connect_last_error}</p>
          )}
          {connected && stripeConnect?.stripe_connect_connected_at && (
            <p className="text-muted small">
              Connected {new Date(stripeConnect.stripe_connect_connected_at).toLocaleDateString()}.
            </p>
          )}

          {!hasConnectedAccount && (
            <div className="alert alert-info mb-3">
              <strong>Before you connect</strong>
              <p className="mb-1">
                Make sure this is the venue&apos;s Stripe account and that Stripe verification is
                complete, card payments are enabled, and a payout bank account has been added.
              </p>
              <p className="mb-0">
                Catch automatically collects its platform fee. No fee setup is required in Stripe.
              </p>
            </div>
          )}

          {requiresSetup && hasConnectedAccount && (
            <p className="alert alert-warning mb-3">
              This Stripe account is not ready to accept payments. Complete the required setup in
              Stripe, then return here and refresh the connection status.
            </p>
          )}

          <div className="d-flex gap-2">
            {!connected && (
              <button
                className="btn btn-primary"
                disabled={isSubmitting}
                onClick={connectAccount}
                type="button"
              >
                {isSubmitting ? "Connecting…" : "Connect Stripe account"}
              </button>
            )}
            {hasConnectedAccount && (
              <button
                className="btn btn-outline-danger"
                disabled={isSubmitting}
                onClick={disconnect}
                type="button"
              >
                Disconnect Stripe
              </button>
            )}
            <button
              className="btn btn-outline-secondary"
              disabled={isSubmitting}
              onClick={loadStatus}
              type="button"
            >
              Refresh status
            </button>
          </div>
        </>
      )}
    </section>
  )
}

export default StripeConnectSection
